import { Flex } from "components/Flex";
import { SelectModified } from "components/SelectModified";
import { FormikErrors, useFormik } from "formik";
import React, { useState } from "react";
import * as Yup from "yup";
import { useGraphQLAuth } from "../../hooks/useGraphQLAuth";
import { useToast } from "../../hooks/useToast";
import { Button } from "../Button";
import { CheckBox } from "../CheckBox";
import { InputFormField } from "../InputFormField";
import { Question } from "../QuestionsList";
import { Title } from "../Title";
import {
  ErrorMessage,
  FormActions,
  FormContainer,
  FormContent,
  OptionActions,
  OptionItem,
  OptionsContainer,
} from "./styles";

interface QuestionUpdateFormProps {
  question: Question;
  onSuccess?: (updatedQuestion: Question) => void;
  onCancel: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Nome é obrigatório")
    .min(3, "Nome deve ter pelo menos 3 caracteres"),
  category: Yup.string().optional(),
  scoreWeight: Yup.number()
    .required("Peso da pontuação é obrigatório")
    .min(0, "Peso deve ser maior ou igual a 0")
    .max(100, "Peso deve ser menor ou igual a 100"),
  type: Yup.string().required("Tipo é obrigatório"),
  allowMultipleAnswers: Yup.boolean(),
  nextQuestionId: Yup.string().optional(),
  options: Yup.array().of(
    Yup.object({
      id: Yup.string().optional(),
      text: Yup.string().required("Texto da opção é obrigatório"),
      isCorrect: Yup.boolean(),
      score: Yup.number().min(0, "Score deve ser maior ou igual a 0"),
      nextQuestionId: Yup.string().optional(),
    })
  ),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const QuestionUpdateForm: React.FC<QuestionUpdateFormProps> = ({
  question,
  onSuccess,
  onCancel,
}) => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = {
    name: question.name || "",
    category: question.category || "",
    scoreWeight: question.scoreWeight || 0,
    type: question.type || "MULTIPLE_CHOICE",
    allowMultipleAnswers: question.allowMultipleAnswers || false,
    nextQuestionId: question.nextQuestionId || "",
    options:
      question.options?.map((option) => ({
        id: option.id,
        text: option.text,
        isCorrect: option.isCorrect,
        score: option.score || 0,
        nextQuestionId: option.nextQuestionId || "",
      })) || [],
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      // Custom validation for MULTIPLE_CHOICE questions
      if (
        values.type === "MULTIPLE_CHOICE" &&
        values.options &&
        values.options?.length < 2
      ) {
        setError(
          "Pelo menos duas opções são obrigatórias para múltipla escolha"
        );
        setIsLoading(false);
        return;
      }

      // Mark options as touched to trigger validation
      setFieldTouched("options", true);

      try {
        const response = await connectGraphQLApi({
          query: `
            mutation BackofficeUpdateQuestion($input: UpdateQuestionInput!) {
              backofficeUpdateQuestion(input: $input) {
                questionId
                questionnaireId
                name
                category
                scoreWeight
                type
                allowMultipleAnswers
                nextQuestionId
                options {
                  id
                  text
                  isCorrect
                  score
                  nextQuestionId
                }
                status
                updateDate
              }
            }
          `,
          variables: {
            input: {
              questionId: question.questionId,
              name: values.name.trim(),
              category: values.category?.trim(),
              scoreWeight: values.scoreWeight,
              type: values.type,
              allowMultipleAnswers: values.allowMultipleAnswers,
              nextQuestionId: values.nextQuestionId?.trim() || null,
              options: values.options?.map((option) => ({
                id: option.id || "",
                text: option.text.trim(),
                isCorrect: option.isCorrect,
                score: option.score,
                nextQuestionId: option.nextQuestionId?.trim() || null,
              })),
            },
          },
          errorMessage: "Falha ao atualizar pergunta",
          useNewApi: true,
        });

        setToast({
          type: "success",
          message: "Pergunta atualizada com sucesso!",
          isActive: true,
          timeToRemoveToast: 3000,
        });

        if (onSuccess) {
          onSuccess(response.data.backofficeUpdateQuestion);
        }
      } catch (error: any) {
        console.error("Error updating question:", error);
        setError(error?.message || "Erro ao atualizar pergunta");
        setToast({
          type: "error",
          message: error?.message || "Erro ao atualizar pergunta",
          isActive: true,
          timeToRemoveToast: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const addOption = () => {
    // Don't allow adding options for TRUE_FALSE type
    if (values.type === "TRUE_FALSE") return;

    const newOption = {
      id: `temp_${Date.now()}`,
      text: "",
      isCorrect: false,
      score: 0,
      nextQuestionId: "",
    };
    setFieldValue("options", [...(values.options || []), newOption]);
  };

  const removeOption = (index: number) => {
    // Don't allow removing options for TRUE_FALSE type
    if (values.type === "TRUE_FALSE") return;

    const newOptions = values.options?.filter((_, i) => i !== index);
    setFieldValue("options", newOptions);
  };

  const updateOption = (index: number, field: string, value: any) => {
    const newOptions = [...(values.options || [])];
    newOptions[index] = { ...newOptions[index], [field]: value };

    // If marking an option as correct, uncheck all other options
    if (field === "isCorrect" && value === true) {
      newOptions.forEach((option, i) => {
        if (i !== index) {
          option.isCorrect = false;
        }
      });
    }

    setFieldValue("options", newOptions);
  };

  // Handle type change to initialize TRUE_FALSE options
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    handleChange(e);

    if (newType === "TRUE_FALSE") {
      // Initialize with predefined TRUE_FALSE options
      const trueFalseOptions = [
        {
          id: "temp_true_option",
          text: "Verdadeiro",
          isCorrect: false,
          score: 0,
          nextQuestionId: "",
        },
        {
          id: "temp_false_option",
          text: "Falso",
          isCorrect: false,
          score: 0,
          nextQuestionId: "",
        },
      ];
      setFieldValue("options", trueFalseOptions);
    } else {
      // Clear options when changing from TRUE_FALSE to another type
      setFieldValue("options", []);
    }

    // Mark options as touched when changing to MULTIPLE_CHOICE to trigger validation
    if (newType === "MULTIPLE_CHOICE") {
      setFieldTouched("options", true);
    }
  };

  const questionTypes = [
    { value: "MULTIPLE_CHOICE", description: "Múltipla escolha" },
    { value: "TRUE_FALSE", description: "Verdadeiro/Falso" },
    { value: "FREE_TEXT", description: "Texto livre" },
  ];

  return (
    <FormContainer>
      <Title elementHTML="h3" className="subtitle" text="Editar Pergunta" />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <FormContent onSubmit={handleSubmit}>
        <Flex direction="column">
          <InputFormField
            label="Nome da pergunta"
            name="name"
            value={values.name}
            onChange={handleChange}
            error={touched.name && !!errors.name}
            mensageError={errors.name}
            block
            marginBotton="20"
          />
          <Flex gap="1rem">
            <InputFormField
              label="Categoria"
              name="category"
              value={values.category}
              onChange={handleChange}
              error={touched.category && !!errors.category}
              mensageError={errors.category}
              marginBotton="20"
              block
            />
            <InputFormField
              label="Peso da pontuação"
              name="scoreWeight"
              type="number"
              value={values.scoreWeight.toString()}
              onChange={handleChange}
              error={touched.scoreWeight && !!errors.scoreWeight}
              mensageError={errors.scoreWeight}
              width="200px"
              marginBotton="20"
            />
          </Flex>

          <SelectModified
            name="type"
            label="Tipo de pergunta"
            value={values.type}
            options={questionTypes.map((t) => ({
              value: t.value,
              label: t.description,
            }))}
            onChange={handleTypeChange}
            error={touched.type && !!errors.type}
            errorMessage={errors.type}
            block
          />
        </Flex>

        {values.type !== "FREE_TEXT" && (
          <>
            <Title elementHTML="h2" className="subtitle-medium" text="Opções" />
            <Flex direction="column">
              <OptionsContainer>
                {values.options?.map((option, index) => (
                  <OptionItem key={option.id}>
                    <Flex gap="1rem">
                      <InputFormField
                        label="Texto"
                        name={`options[${index}].text`}
                        value={option.text}
                        onChange={(e) =>
                          updateOption(index, "text", e.target.value)
                        }
                        block
                        marginBotton="10"
                        disabled={values.type === "TRUE_FALSE"}
                        error={touched.options && !!errors.options}
                        mensageError={
                          (
                            (errors as FormikErrors<FormValues>)
                              .options as FormValues["options"]
                          )?.[index]?.text
                        }
                      />
                      <InputFormField
                        label="Pontuação"
                        name={`options[${index}].score`}
                        type="number"
                        value={option.score?.toString() || "0"}
                        onChange={(e) =>
                          updateOption(
                            index,
                            "score",
                            parseInt(e.target.value) || 0
                          )
                        }
                        width="130px"
                        marginBotton="10"
                      />
                    </Flex>
                    <CheckBox
                      label="Resposta correta"
                      name={`options[${index}].isCorrect`}
                      value={option.isCorrect}
                      checked={option.isCorrect}
                      onChange={(event) =>
                        updateOption(index, "isCorrect", event.checked)
                      }
                    />
                    {values.type !== "TRUE_FALSE" && (
                      <OptionActions>
                        <Button
                          label="Remover"
                          typeBtn="danger"
                          size="small"
                          onClick={() => removeOption(index)}
                          type="button"
                        />
                      </OptionActions>
                    )}
                  </OptionItem>
                ))}
              </OptionsContainer>
            </Flex>
            {values.type !== "TRUE_FALSE" && (
              <Button
                label="Adicionar opção"
                typeBtn="outline"
                size="small"
                onClick={addOption}
                type="button"
              />
            )}
          </>
        )}

        <FormActions>
          <Button
            label="Cancelar"
            typeBtn="outline"
            onClick={onCancel}
            type="button"
          />
          <Button
            label="Salvar"
            typeBtn="blue"
            type="submit"
            load={isLoading}
            disable={isSubmitting}
          />
        </FormActions>
      </FormContent>
    </FormContainer>
  );
};
